import posthog from 'posthog-js';

// PostHog runs in production. In local dev it can be opted in only to load the
// toolbar (NEXT_PUBLIC_POSTHOG_DEV=true) — but it sends NOTHING: all events are
// dropped (before_send), exception capture is off, and session replay is off.
// This avoids polluting the production project, since dev reuses the same key.
const isProductionEnvironment =
  process.env.NODE_ENV === 'production' &&
  process.env.VERCEL_ENV !== 'preview';

const isDevPosthog = process.env.NEXT_PUBLIC_POSTHOG_DEV === 'true';

let initialized = false;

/**
 * Initializes PostHog. Must run AFTER React hydration: posthog-js injects its
 * remote-config <script> relative to `body > script`, so initializing during
 * the client bootstrap (instrumentation-client.ts) puts an unexpected node in
 * the <body> before React hydrates — which triggers a hydration mismatch and
 * makes React discard the server HTML and re-render the whole tree.
 * Called from <PostHogInit /> inside an effect.
 */
export const initPosthog = () => {
  if (initialized) return;
  if (!isProductionEnvironment && !isDevPosthog) return;
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;

  initialized = true;

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: '/ingest',
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.posthog.com',
    defaults: '2025-11-30',
    // Dev: drop every event before it leaves the browser (toolbar still loads).
    before_send: isDevPosthog ? () => null : undefined,
    // Dev: don't feed local exceptions into Error Tracking.
    capture_exceptions: !isDevPosthog,
    autocapture: true,
    capture_pageleave: true,
    capture_pageview: false,
    // Capture Core Web Vitals (LCP, FCP, CLS, INP) as $web_vitals events.
    capture_performance: { web_vitals: true },
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
    // Dev: no session replay recording locally.
    disable_session_recording: isDevPosthog,
    session_recording: {
      // LGPD: mask form inputs by default (CPF, CNH, email, phone, password).
      // Page text stays readable so replays remain useful.
      maskAllInputs: true,
      // Opt-out: add class "ph-allow-capture" to a non-sensitive input to record it.
      maskInputFn: (text, element) =>
        element?.classList?.contains('ph-allow-capture') ? text : '*'.repeat(text.length),
    },
  });

  posthog.register({
    user_agent: navigator.userAgent,
    environment: process.env.VERCEL_ENV || process.env.NODE_ENV,
    // Tie events to the deploy that produced them (matches the source-map
    // release version uploaded at build time).
    release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
  });
};
